import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import AppSidebar from './AppSidebar'
import PageHeader from './PageHeader'
import SetupGuardBanner from './SetupGuardBanner'
import './AppLayout.css'

interface Props {
  children: React.ReactNode
}

const AppLayout: React.FC<Props> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileOpen) {
      // Return focus to the menu trigger when the drawer closes (but not on
      // the initial mount, only after it had actually been opened).
      if (wasOpenRef.current) menuButtonRef.current?.focus()
      wasOpenRef.current = false
      return
    }
    wasOpenRef.current = true
    // Move focus into the drawer so keyboard/screen-reader users start there.
    const firstLink = document.querySelector<HTMLElement>('.app-sidebar [role="link"], .app-sidebar a, .app-sidebar button')
    firstLink?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mobileOpen])

  return (
    <div className="app-shell">
      <div
        className={`app-sidebar-backdrop ${mobileOpen ? 'is-open' : ''}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
      <AppSidebar className={`app-sidebar ${mobileOpen ? 'is-open' : ''}`} />
      <main className="app-main">
        <PageHeader
          menuButtonRef={menuButtonRef}
          mobileOpen={mobileOpen}
          onMenuToggle={() => setMobileOpen(v => !v)}
        />
        <SetupGuardBanner />
        <div className="app-content">
          {children}
        </div>
      </main>
    </div>
  )
}

export default AppLayout
